<template>
  <RulesFormSettings
    v-if="!isChanges"
    v-model:criteria-model="criteriaModel"
    v-model:actions-model="actionsModel"
    :map-criteria
    :disabled
  >
    <template #criteria>
      <slot name="criteria" />
    </template>
    <template #actions="slotProps">
      <slot
        name="actions"
        v-bind="slotProps"
      />
    </template>
    <template #before>
      <UIButton
        :disabled="previewDisabled || disabled"
        label="Preview changes"
        variant="secondary"
        class="sm:mr-auto"
        @click="handleClickPreview"
      />
    </template>
    <template #after>
      <RulesFormButtons
        v-model="applyToFutureOnly"
        :rule-id
        :disabled="buttonsDisabled"
        :loading="disabled"
        @apply="handleApply"
        @update="handleUpdate"
        @create="handleCreate"
      />
    </template>
  </RulesFormSettings>
  <RulesFormChanges
    v-else
    :data="ruleData"
    @fetch="handleFetchTransactions"
  >
    <template #change="slotProps">
      <slot
        name="change"
        v-bind="slotProps"
      />
    </template>
    <template
      v-if="!readonly"
      #before
    >
      <UIButton
        label="Rule settings"
        variant="secondary"
        class="sm:mr-auto"
        :disabled
        @click="handleClickEdit"
      />
    </template>
    <template
      v-if="!readonly"
      #after
    >
      <RulesFormButtons
        v-model="applyToFutureOnly"
        :rule-id
        :disabled="buttonsDisabled"
        :loading="disabled"
        :prefilled
        @apply="handleApply"
        @update="handleUpdate"
        @create="handleCreate"
      />
    </template>
  </RulesFormChanges>
</template>

<script setup lang="ts" generic="T">
import { computed, inject, ref, shallowRef, watch } from 'vue'
import { cloneDeep, isEqual } from 'lodash'

import {
  TransactionRule,
  TransactionRuleCriteria,
  PaginatedResponse,
  LinkedDataTransaction,
  ReadonlyMode,
  TransactionRuleCriteriaType,
  TransactionRuleCriteriaLegacy,
  FilterLogic
} from '@types'

import { NOTIFICATION_DELAY, READONLY_MODE } from '@/const/common'
import { getDefaultCriteriaModel } from './utils/const'

import { useNotifications } from '@/plugins/notification'
import { getActiveFilters } from '@/components/UI/Filter/utils/helpers'

import RulesFormSettings from './RulesFormSettings.vue'
import RulesFormChanges from './RulesFormChanges.vue'
import RulesFormButtons from './RulesFormButtons.vue'
import { UIButton } from '@ui/buttons'
import { generateRandomKey } from '@/helpers/common'

type Props = {
  rule?: TransactionRule<T>
  isChanges?: boolean
  prefilled?: boolean
  disabled?: boolean
  getFilledActions: (action: T) => T
  mapCriteria?: (criteria: TransactionRuleCriteriaType[]) => TransactionRuleCriteriaType[]
}

type Emits = {
  'fetch:transactions': [
    data: TransactionRule<T>['data'],
    pageNo: number,
    pageSize: number,
    callback: (data: PaginatedResponse<LinkedDataTransaction>) => void,
    signal?: AbortSignal,
  ]
  create: [
    rule: TransactionRule<T>['data'],
    apply_to_existing_transactions: boolean,
    successCallback: (customMessage?: string) => void,
    errorCallback: () => void,
  ]
  update: [
    id: string,
    data: TransactionRule<T>,
    successCallback: () => void,
    errorCallback: () => void,
  ]
  apply: [
    data: TransactionRule<T>['data'],
    successCallback: (customMessage?: string) => void,
    errorCallback: () => void,
  ]
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { progress, remove, update } = useNotifications()

const readonly = inject<ReadonlyMode>(READONLY_MODE)
const isChanges = ref(props.isChanges)

const applyToFutureOnly = ref(false)

const criteriaModel = ref<TransactionRuleCriteria>(getDefaultCriteriaModel())
const actionsModel = shallowRef<T>()

const ruleId = computed(() => props.rule?.id)

const ruleData = computed(() => ({
  criteria: filledCriteria.value || getDefaultCriteriaModel(),
  actions: filledActions.value,
}))

const currentRuleData = computed(() => props.rule?.data)
const currentRuleCriteria = computed(
  () => props.rule?.data?.criteria || getDefaultCriteriaModel(),
)
const currentRuleActions = computed(() => props.rule?.data?.actions)

const filledCriteria = computed(() => getActiveFilters(criteriaModel.value))
const filledActions = computed(
  () => actionsModel.value && props.getFilledActions(actionsModel.value),
)

const isCriteriaEmpty = computed(() => !filledCriteria.value?.params?.length)
const isActionsEmpty = computed(() => {
  if (Array.isArray(filledActions.value)) {
    return filledActions.value?.length === 0
  }
  return !filledActions.value || Object.keys(filledActions.value).length === 0
})

// Connector scoping is a normal id-based criterion inside data.criteria now, so it participates in
// the standard criteria change-detection below — no special hoist/reconstruct handling.
const isCriteriaChanged = computed(
  () => !isEqual(filledCriteria.value, currentRuleCriteria.value),
)
const isActionsChanged = computed(
  () => !isEqual(filledActions.value, currentRuleActions.value),
)
const previewDisabled = computed(
  () => isCriteriaEmpty.value || isActionsEmpty.value,
)

const buttonsDisabled = computed(
  () =>
    previewDisabled.value ||
    (!!ruleId.value && !isCriteriaChanged.value && !isActionsChanged.value),
)

const normalizeLegacyCriteria = (criteria: TransactionRuleCriteriaLegacy[]): TransactionRuleCriteria => {
  return {
    logic: FilterLogic.AND,
    params: criteria.map(param => ({
      key: generateRandomKey(),
      logic: param.logic,
      params: param.params.map(p => ({
        key: generateRandomKey(),
        field: param.type,
        comparison: p.comparison,
        value: p.value,
      })),
    })),
  }
}

const prepareCriteria = (criteria: TransactionRuleCriteria | TransactionRuleCriteriaLegacy[]) => {
  const isLegacyCriteria = Array.isArray(criteria)
  if (isLegacyCriteria) {
    return normalizeLegacyCriteria(criteria)
  }
  const data = cloneDeep(criteria)
  data.params = data.params.map(param => {
    return {
      ...param,
      key: generateRandomKey(),
      params: param.params.map(p => ({ ...p, key: generateRandomKey() }))
    }
  })
  return data
}

const initData = () => {
  criteriaModel.value = prepareCriteria(currentRuleCriteria.value)
  actionsModel.value = cloneDeep(currentRuleActions.value)
}

const handleFetchTransactions = async (
  data: TransactionRule<T | undefined>['data'],
  pageNo: number,
  pageSize: number,
  callback: (data: PaginatedResponse<LinkedDataTransaction>) => void,
  signal?: AbortSignal,
) => {
  if (!data.actions) return
  emit(
    'fetch:transactions',
    data as TransactionRule<T>['data'],
    pageNo,
    pageSize,
    callback,
    signal,
  )
}

const handleCreate = async (event?: MouseEvent) => {
  event?.stopImmediatePropagation()
  if (!ruleData.value.actions) return
  const nid = await progress({
    message: 'Creating rule',
  })
  emit(
    'create',
    ruleData.value as TransactionRule<T>['data'],
    !applyToFutureOnly.value,
    async (customMessage?: string) => {
      await update(
        nid,
        {
          type: 'success',
          message: customMessage || 'Rule created',
        },
        NOTIFICATION_DELAY,
      )
      handleClose()
    },
    async () => {
      await remove(nid)
      handleClose()
    },
  )
}

const handleUpdate = async (event?: MouseEvent) => {
  if (!ruleId.value || !ruleData.value.actions || !props.rule) return
  event?.stopImmediatePropagation()
  const nid = await progress({
    message: 'Updating rule',
  })
  emit(
    'update',
    ruleId.value,
    {
      ...props.rule,
      data: ruleData.value as TransactionRule<T>['data'],
      apply_to_existing_transactions: !applyToFutureOnly.value,
      type: props.rule.type
    },
    async () => {
      await update(
        nid,
        {
          type: 'success',
          message: 'Rule updated',
        },
        NOTIFICATION_DELAY,
      )
      handleClose()
    },
    async () => {
      await remove(nid)
      handleClose()
    },
  )
}

const handleApply = async () => {
  if (!currentRuleData.value) return
  const nid = await progress({
    message: 'Apply changes',
  })
  emit(
    'apply',
    currentRuleData.value,
    async (customMessage?: string) => {
      await update(
        nid,
        {
          type: 'success',
          message: customMessage || 'Changes applied',
        },
        NOTIFICATION_DELAY,
      )
      handleClose()
    },
    async () => {
      await remove(nid)
      handleClose()
    },
  )
}

const handleClickPreview = () => {
  isChanges.value = true
}

const handleClickEdit = () => {
  isChanges.value = false
}

const handleClose = () => {
  emit('close')
}

const resetData = () => {
  criteriaModel.value = getDefaultCriteriaModel()
  actionsModel.value = undefined
}

watch(
  ruleId,
  value => {
    value || props.prefilled ? initData() : resetData()
  },
  { immediate: true },
)
</script>

<style>
.module-rules-form {
  &__title {
    @apply flex flex-col;
    @apply py-2;
  }

  &__criteria {
    @apply text-sm sm:text-xs;
    @apply text-gray-400 dark:text-gray-500;
  }
}
</style>
